import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { useProcedureExecutionStore } from '@/stores/procedure-execution.store';
import { StepExecutionCard } from './StepExecutionCard';
import type { SectionExecution } from '../../services/procedure-execution.service';

interface Props {
  inspectionId: string;
  section: SectionExecution;
  sectionIndex: number;
  globalStepOffset: number;
  canExecute: boolean;
}

export function SectionAccordionItem({
  inspectionId,
  section,
  sectionIndex,
  globalStepOffset,
  canExecute,
}: Props) {
  const expandedSections = useProcedureExecutionStore((s) => s.expandedSections);
  const toggleSection = useProcedureExecutionStore((s) => s.toggleSection);

  const isOpen = expandedSections.has(section.sectionSnapshotId);
  const allComplete = section.totalSteps > 0 && section.completedSteps === section.totalSteps;

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      {/* Section header */}
      <button
        type="button"
        onClick={() => toggleSection(section.sectionSnapshotId)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left bg-muted/20 hover:bg-muted/40 transition min-h-[48px]"
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon
          icon={isOpen ? faChevronDown : faChevronRight}
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform"
        />

        <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-mono text-muted-foreground shrink-0">
              {sectionIndex + 1}.
            </span>
            <span className="text-sm font-semibold text-foreground truncate">{section.title}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {section.completedSteps} / {section.totalSteps}
            </span>
            {allComplete && (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="h-4 w-4 text-green-600"
                title="All steps complete"
              />
            )}
          </div>
        </div>
      </button>

      {/* Steps list */}
      {isOpen && (
        <div className="space-y-2 p-3 bg-background">
          {section.steps.map((step, stepIdx) => (
            <StepExecutionCard
              key={step.stepExecutionId}
              inspectionId={inspectionId}
              step={step}
              stepIndex={globalStepOffset + stepIdx + 1}
              canExecute={canExecute}
            />
          ))}
        </div>
      )}
    </div>
  );
}
